import { useEffect, useState } from "react";
import { Upload, FileText, X } from "lucide-react";
import { api, formatApiError } from "../api/client";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";

const QUICK_VALIDITY = [
  { label: "No caduca", value: "" },
  { label: "30 días", value: "30" },
  { label: "90 días", value: "90" },
  { label: "1 año", value: "365" },
  { label: "Personalizado", value: "custom" },
];

const MAX = 50 * 1024 * 1024;
const ALLOWED_EXT = ["pdf", "doc", "docx", "xls", "xlsx", "png", "jpg", "jpeg", "txt", "csv", "zip", "eml", "msg"];

export default function EvidenceUploadModal({ open, onClose, onUploaded, presetControlId }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [collectedAt, setCollectedAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [validityChoice, setValidityChoice] = useState("");
  const [customDays, setCustomDays] = useState("");
  const [tagsInput, setTagsInput] = useState("");
  const [controls, setControls] = useState([]); // [{id, code, title}]
  const [selectedCtrls, setSelectedCtrls] = useState(presetControlId ? [presetControlId] : []);
  const [searchCtrl, setSearchCtrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/control-implementations?framework=iso27001&page=1&page_size=200");
      setControls((data.items || []).map((i) => ({
        id: i.id, code: i.control_code, title: i.control?.title || i.control_code,
      })));
    })();
  }, []);

  const onPickFile = (f) => {
    if (!f) return;
    const ext = f.name.split(".").pop().toLowerCase();
    if (!ALLOWED_EXT.includes(ext)) return toast.error(`Tipo no permitido. Aceptados: ${ALLOWED_EXT.join(", ")}`);
    if (f.size > MAX) return toast.error("El fichero supera el tamaño máximo de 50 MB.");
    setFile(f);
    if (!title.trim()) setTitle(f.name.replace(/\.[^.]+$/, ""));
  };

  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    onPickFile(e.dataTransfer.files?.[0]);
  };

  const filteredCtrls = searchCtrl
    ? controls.filter((c) => c.code.toLowerCase().includes(searchCtrl.toLowerCase()) || c.title.toLowerCase().includes(searchCtrl.toLowerCase()))
    : controls.slice(0, 30);

  const toggleCtrl = (id) => {
    setSelectedCtrls((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const validityDays = validityChoice === "custom" ? parseInt(customDays || "0", 10) : (validityChoice ? parseInt(validityChoice, 10) : null);

  const submit = async () => {
    if (!file) return toast.error("Selecciona un fichero.");
    if (!title.trim()) return toast.error("Indica un título.");
    if (selectedCtrls.length === 0) return toast.error("Asocia al menos un control.");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title);
      fd.append("description", description);
      fd.append("related_control_ids", JSON.stringify(selectedCtrls));
      fd.append("collected_at", new Date(collectedAt).toISOString());
      if (validityDays && validityDays > 0) fd.append("validity_period_days", String(validityDays));
      const tags = tagsInput.split(",").map((t) => t.trim()).filter(Boolean);
      fd.append("tags", JSON.stringify(tags));
      const { data } = await api.post("/evidences", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Evidencia subida.");
      onUploaded(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="modal-upload-evidence">
        <DialogHeader><DialogTitle>Subir evidencia</DialogTitle></DialogHeader>

        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-md p-6 text-center ${dragOver ? "border-primary bg-primary/5" : "border-slate-200"}`}
        >
          {!file ? (
            <>
              <Upload className="h-8 w-8 mx-auto text-slate-400" />
              <p className="text-[14px] text-slate-700 mt-2">Arrastra un fichero aquí o</p>
              <label className="text-primary font-medium cursor-pointer hover:underline text-[14px]" data-testid="evidence-file-label">
                selecciona uno
                <input type="file" className="hidden" onChange={(e) => onPickFile(e.target.files?.[0])} data-testid="evidence-file-input" />
              </label>
              <p className="text-[12px] text-slate-400 mt-2">Máx. 50 MB. Tipos: {ALLOWED_EXT.join(", ")}</p>
            </>
          ) : (
            <div className="flex items-center justify-between text-left">
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-primary" />
                <div>
                  <div className="font-medium text-slate-900 text-[14px]">{file.name}</div>
                  <div className="text-[12px] text-slate-500">{(file.size / 1024 / 1024).toFixed(2)} MB</div>
                </div>
              </div>
              <button onClick={() => setFile(null)} className="text-slate-400 hover:text-destructive"><X className="h-4 w-4" /></button>
            </div>
          )}
        </div>

        <div className="space-y-3 mt-4">
          <div>
            <Label>Título</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="evidence-title" />
          </div>
          <div>
            <Label>Descripción</Label>
            <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="evidence-description" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Fecha de obtención</Label>
              <Input type="date" value={collectedAt} onChange={(e) => setCollectedAt(e.target.value)} data-testid="evidence-collected" />
            </div>
            <div>
              <Label>Validez</Label>
              <Select value={validityChoice} onValueChange={setValidityChoice}>
                <SelectTrigger data-testid="evidence-validity"><SelectValue placeholder="No caduca" /></SelectTrigger>
                <SelectContent>
                  {QUICK_VALIDITY.map((v) => <SelectItem key={v.label} value={v.value || "none"}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
              {validityChoice === "custom" && (
                <Input type="number" min={1} className="mt-1" placeholder="Días" value={customDays} onChange={(e) => setCustomDays(e.target.value)} data-testid="evidence-custom-days" />
              )}
            </div>
          </div>
          <div>
            <Label>Etiquetas (separadas por comas)</Label>
            <Input placeholder="rgpd, q2-2026" value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} data-testid="evidence-tags" />
          </div>
          <div>
            <Label>Controles asociados ({selectedCtrls.length})</Label>
            <Input placeholder="Buscar control..." value={searchCtrl} onChange={(e) => setSearchCtrl(e.target.value)} data-testid="evidence-ctrl-search" className="mb-2" />
            <div className="max-h-48 overflow-y-auto border border-slate-200 rounded p-2 space-y-1">
              {filteredCtrls.map((c) => {
                const checked = selectedCtrls.includes(c.id);
                return (
                  <label key={c.id} className="flex items-center gap-2 text-[13px] cursor-pointer hover:bg-slate-50 p-1 rounded">
                    <input type="checkbox" checked={checked} onChange={() => toggleCtrl(c.id)} data-testid={`evidence-ctrl-${c.code}`} />
                    <span className="font-medium text-slate-700">{c.code}</span>
                    <span className="text-slate-500 truncate">{c.title}</span>
                  </label>
                );
              })}
              {filteredCtrls.length === 0 && <div className="text-[12px] text-slate-400 p-2">Sin resultados.</div>}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={busy || !file} data-testid="evidence-submit">{busy ? "Subiendo..." : "Guardar evidencia"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
